import {StyleSheet, css} from 'aphrodite';
import * as d3 from 'd3';
import React, {useEffect, useMemo, useState} from 'react';

import {fetchSignedFileUrl, getChatFilePreviewUrl} from 'llm/chat-library/ChatDAO';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import {MonacoLanguage} from 'llm/monaco/MonacoTypes';
import MolstarViewer, {MolstarWrapperProps} from 'molbio/molstar/Viewer';
import {FONT_FAMILY_MONOSPACED, FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import MarkdownContent from 'ui-library/MarkdownContent';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import {CenteredSpinner} from 'ui-library/Spinner';

const DISPLAY_ROW_LIMIT = 1000;

const MOLSTAR_MIME_TYPE_TO_FORMAT: Record<string, MolstarWrapperProps['format']> = {
  'chemical/x-pdb': 'pdb',
  'chemical/x-pdbqt': 'pdbqt',
  'chemical/x-mmcif': 'mmcif',
};

interface FileViewerBodyProps {
  chatSessionId: string;
  externalFileId: string;
  mimeType: string;
}

/** Hook to fetch text content from a URL. Skips fetching when url is null. */
export function useFileContent(url: string | null): {
  content: string | null;
  isLoading: boolean;
  error: string | null;
} {
  const [content, setContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (url == null) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setContent(null);
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then((text: string) => {
        if (!cancelled) {
          setContent(text);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load file content.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return {content, isLoading, error};
}

/** Resolves a signed URL for a chat file, then fetches and displays its text content. */
function TextContentLoader({
  chatSessionId,
  externalFileId,
  children,
}: {
  chatSessionId: string;
  externalFileId: string;
  children: (content: string) => JSX.Element;
}): JSX.Element {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSignedUrl(null);
    setUrlError(null);
    fetchSignedFileUrl(chatSessionId, externalFileId)
      .then((url) => {
        if (!cancelled) {
          setSignedUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrlError('Failed to load file content.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chatSessionId, externalFileId]);

  const {content, isLoading, error} = useFileContent(signedUrl);

  if (urlError) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={urlError} />;
  }
  if (signedUrl == null) {
    return <CenteredSpinner />;
  }

  if (isLoading) {
    return <CenteredSpinner />;
  }
  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />;
  }

  return children(content ?? '');
}

function TextFileContent({
  chatSessionId,
  externalFileId,
}: {
  chatSessionId: string;
  externalFileId: string;
}): JSX.Element {
  return (
    <TextContentLoader chatSessionId={chatSessionId} externalFileId={externalFileId}>
      {(content) => (
        <textarea
          className={css(styles.textPreview)}
          value={content}
          readOnly={true}
          placeholder="Empty file"
        />
      )}
    </TextContentLoader>
  );
}

function MarkdownFileContent({
  chatSessionId,
  externalFileId,
}: {
  chatSessionId: string;
  externalFileId: string;
}): JSX.Element {
  return (
    <TextContentLoader chatSessionId={chatSessionId} externalFileId={externalFileId}>
      {(content) => (
        <div className={css(styles.markdownPreview)}>
          <MarkdownContent content={content} />
        </div>
      )}
    </TextContentLoader>
  );
}

function CodeFileContent({
  chatSessionId,
  externalFileId,
  language,
}: {
  chatSessionId: string;
  externalFileId: string;
  language: MonacoLanguage;
}): JSX.Element {
  return (
    <TextContentLoader chatSessionId={chatSessionId} externalFileId={externalFileId}>
      {(content) => (
        <MonacoEditor
          width="100%"
          height="100%"
          value={content}
          language={language}
          theme="vs"
          options={{readOnly: true, minimap: {enabled: false}}}
        />
      )}
    </TextContentLoader>
  );
}

function CSVFileContent({
  chatSessionId,
  externalFileId,
}: {
  chatSessionId: string;
  externalFileId: string;
}): JSX.Element {
  return (
    <TextContentLoader chatSessionId={chatSessionId} externalFileId={externalFileId}>
      {(content) => <CSVTable content={content} />}
    </TextContentLoader>
  );
}

function CSVTable({content}: {content: string}): JSX.Element {
  const csvRows = useMemo(() => d3.csvParseRows(content), [content]);

  if (!csvRows || csvRows.length === 0) {
    return <InlineBanner bannerType={InlineBannerType.INFO} text="The CSV file is empty." />;
  }

  const headerRow = csvRows[0];
  const allDataRows = csvRows.slice(1);
  const totalRows = allDataRows.length;
  const displayedRows = allDataRows.slice(0, DISPLAY_ROW_LIMIT);
  const hasMoreRows = totalRows > DISPLAY_ROW_LIMIT;

  return (
    <div className={css(styles.tableWrapper)}>
      {hasMoreRows && (
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text={`Showing the first ${DISPLAY_ROW_LIMIT} rows out of ${totalRows}`}
          supportingInfo="Download to view the complete data."
        />
      )}
      <Table
        calculateInView={true}
        className={css(styles.table)}
        emptyTableText="No data"
        fitContent={true}
        frozenHeader={true}
        getRowKey={(rowIndex: number) => rowIndex}
        isCompact={true}
        numRows={displayedRows.length}
        overflowVisible={true}
        showBorder={false}
      >
        {headerRow.map((header, columnIndex) => (
          <Column
            key={columnIndex}
            header={header}
            renderCell={(rowIndex: number) => (
              <Cell className={css(styles.cell)}>{displayedRows[rowIndex]?.[columnIndex] || ''}</Cell>
            )}
          />
        ))}
      </Table>
    </div>
  );
}

function MolstarFileContent({
  chatSessionId,
  externalFileId,
  mimeType,
}: {
  chatSessionId: string;
  externalFileId: string;
  mimeType: string;
}): JSX.Element {
  const format = MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType];
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSignedUrl(null);
    setError(null);
    fetchSignedFileUrl(chatSessionId, externalFileId)
      .then((url) => {
        if (!cancelled) {
          setSignedUrl(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load structure.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [chatSessionId, externalFileId]);

  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />;
  }
  if (signedUrl == null) {
    return <CenteredSpinner />;
  }
  return <MolstarViewer url={signedUrl} format={format} className={css(styles.molstarViewer)} />;
}

export function FileViewerBody({chatSessionId, externalFileId, mimeType}: FileViewerBodyProps): JSX.Element {
  switch (mimeType) {
    case 'application/json':
    case 'text/plain':
      return <TextFileContent chatSessionId={chatSessionId} externalFileId={externalFileId} />;
    case 'text/x-python-script':
    case 'text/x-python':
      return (
        <CodeFileContent chatSessionId={chatSessionId} externalFileId={externalFileId} language="python" />
      );
    case 'text/markdown':
      return <MarkdownFileContent chatSessionId={chatSessionId} externalFileId={externalFileId} />;
    case 'text/csv':
      return <CSVFileContent chatSessionId={chatSessionId} externalFileId={externalFileId} />;
    case 'image/jpeg':
    case 'image/png':
      return (
        <img
          src={getChatFilePreviewUrl(chatSessionId, externalFileId)}
          className={css(styles.image)}
          alt="File preview"
        />
      );
    case 'chemical/x-pdb':
    case 'chemical/x-pdbqt':
    case 'chemical/x-mmcif':
      return (
        <MolstarFileContent
          chatSessionId={chatSessionId}
          externalFileId={externalFileId}
          mimeType={mimeType}
        />
      );
    default:
      return (
        <div className={css(styles.unsupported)}>
          <InlineBanner bannerType={InlineBannerType.INFO} text="Preview not available for this file type." />
        </div>
      );
  }
}

const styles = StyleSheet.create({
  textPreview: {
    ...FONT_PROXIMA_13,
    width: '100%',
    height: '100%',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    resize: 'none',
    padding: 12,
    fontFamily: FONT_FAMILY_MONOSPACED,
    backgroundColor: GRAY_LIGHTER,
    color: NEAR_BLACK,
    boxSizing: 'border-box',
  },
  markdownPreview: {
    ...FONT_PROXIMA_13,
    padding: 12,
  },
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    boxSizing: 'border-box',
  },
  table: {
    borderRadius: 0,
    margin: 0,
  },
  cell: {
    ...FONT_PROXIMA_13,
    height: 36,
  },
  image: {
    maxWidth: '100%',
    objectFit: 'contain',
  },
  molstarViewer: {
    width: '100%',
    height: '100%',
    position: 'relative',
    minWidth: 10,
    minHeight: 10,
  },
  unsupported: {
    padding: 16,
  },
});
